/* Copyright (c) 2020-2024 Nordcloud Oy or its affiliates. All Rights Reserved. */

import React from "react";
import { Container, Row, Col } from "react-awesome-styled-grid";
import { Box, Button, Heading, Loader, Modal, Spacer } from "@nordcloud/gnui";
import { QueryPagination, Table } from "~/components";
import { SearchNavbar } from "~/components/Audit/SearchNavbar";
import { useShowError } from "~/components/Error/useShowError";
import { DetailRow } from "~/components/Utils/DetailRow";
import { useAxios, useQueryState } from "~/tools";

type AuditRow = {
  createdAt: string;
  identity: string;
  operation: string;
  url: string;
  payload: object;
};

type LogData = {
  url: string;
  payload: object;
};

const initialLogData = {
  url: "",
  payload: {},
};

function AuditLogSearch() {
  const [
    { limit, page, customerId, startDate, endDate, operation, identity },
  ] = useQueryState();

  const [logs, setLogs] = React.useState([]);
  const [totalCount, setTotalCount] = React.useState(0);
  const [isLoading, setIsLoading] = React.useState(false);
  const [isModalOpen, setIsModalOpen] = React.useState(false);
  const [logPayload, setLogPayload] = React.useState<LogData>(initialLogData);
  const { showError } = useShowError();
  const { Axios } = useAxios();

  const columns = [
    { Header: "Time", accessor: "createdAt", disableSortBy: true },
    { Header: "Identity", accessor: "identity", disableSortBy: true },
    { Header: "Operation", accessor: "operation", disableSortBy: true },
    {
      Header: "",
      accessor: "name",
      Cell: ({ row }: any) => (
        <Button
          icon="search"
          size="sm"
          onClick={() => {
            setLogPayload({
              url: row.original.url,
              payload: row.original.payload,
            });
            setIsModalOpen(true);
          }}
          severity="low"
        >
          View Payload
        </Button>
      ),
      disableSortBy: true,
    },
  ];

  const fetchLogs = React.useCallback(async () => {
    if (!startDate || !endDate) {
      // Prevents fetch on first page entry
      return;
    }

    setIsLoading(true);
    const startTime = startDate + "T00:00:00";
    const endTime = endDate + "T23:59:59";

    const params = {
      customerId,
      startTime,
      endTime,
      operation,
      identity,
      page,
      limit,
    };
    try {
      const { data } = await Axios().get(`/audit-logs`, params);
      setLogs(data?.results);
      setTotalCount(data?.totalCount);
    } catch (e: any) {
      showError(e.response);
    } finally {
      setIsLoading(false);
    }
  }, [Axios, customerId, showError, operation, identity, startDate, endDate]);

  React.useEffect(() => {
    fetchLogs();
  }, [fetchLogs, page, limit]);

  const rows =
    logs?.map((row: AuditRow) => ({
      createdAt: row?.createdAt
        ? new Date(row.createdAt).toISOString().slice(0, 19).replace("T", " ")
        : "",
      identity: row?.identity ?? "",
      operation: row?.operation ?? "",
      url: row?.url ?? "",
      payload: row?.payload ?? {},
    })) || [];

  return (
    <Container>
      <Row>
        <Col>
          <Box spacing="spacing04">
            <Heading level={2}>Audit Logs</Heading>
          </Box>

          <Box>
            <SearchNavbar />
            {isLoading ? (
              <Loader inContent />
            ) : (
              <>
                <Table
                  columns={columns}
                  data={rows || []}
                  defaultSort={[{ id: "datetime", desc: true }]}
                  noDataText="No data available. Adjust filters."
                />
                <QueryPagination count={totalCount} />
              </>
            )}
          </Box>
        </Col>
      </Row>

      <Modal
        contentLabel="Payload Preview"
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        actions={[
          {
            order: 0,
            onAction: () => setIsModalOpen(false),
            label: "Close",
            severity: "medium",
          },
        ]}
      >
        <Container>
          <DetailRow minWidth={"6rem"} title="Url" alignItems="start">
            {logPayload.url || "–"}
          </DetailRow>
          <Spacer />
          <DetailRow minWidth={"6rem"} title="Payload" alignItems="start">
            <pre>{JSON.stringify(logPayload.payload, null, 2)}</pre>
          </DetailRow>
        </Container>
      </Modal>
    </Container>
  );
}

export { AuditLogSearch };
